import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  ExternalMonthlyReportDto,
  InternalMonthlyAnalysisDto,
  MonthlyReportArtifactDto,
  MonthlyReportArtifactListDto,
  MonthlyReportCommercialTermsDto,
  MonthlyReportGenerateDto,
  MonthlyReportGenerateRequest,
  MonthlyReportPagedList,
  MonthlyReportPrecheckDto,
  MonthlyReportProfileDto,
  MonthlyReportVariant,
  ValuationComparisonDto,
} from './types';

/**
 * Monthly Reports API (SPEC v1.0 Slice 1E — see monthly_reports/routes.rs).
 * Both endpoints are batch-nested and READ-ONLY (no mutations).
 *
 *   GET /api/monthly-batches/:batchId/external-monthly-report
 *       gate: report.external_monthly.view  (NO money in the response)
 *   GET /api/monthly-batches/:batchId/internal-monthly-analysis
 *       gate: report.internal_analysis.view (full financials)
 */

export const fetchExternalMonthlyReport = async (
  batchId: string,
): Promise<ExternalMonthlyReportDto> => {
  const res = await apiClient.get<ApiSuccess<ExternalMonthlyReportDto>>(
    `/monthly-batches/${batchId}/external-monthly-report`,
  );
  return res.data.data;
};

export const fetchInternalMonthlyAnalysis = async (
  batchId: string,
): Promise<InternalMonthlyAnalysisDto> => {
  const res = await apiClient.get<ApiSuccess<InternalMonthlyAnalysisDto>>(
    `/monthly-batches/${batchId}/internal-monthly-analysis`,
  );
  return res.data.data;
};

/** FR-011 — pre/post/actual valuation comparison (gate: report.internal_analysis.view). */
export const fetchValuationComparison = async (
  batchId: string,
): Promise<ValuationComparisonDto> => {
  const res = await apiClient.get<ApiSuccess<ValuationComparisonDto>>(
    `/monthly-batches/${batchId}/valuation-comparison`,
  );
  return res.data.data;
};

export const fetchMonthlyReportPrecheck = async (
  batchId: string,
  variant: MonthlyReportVariant,
  locale: 'ja' | 'zh-TW' | 'en',
  recipientCompanyId?: string | null,
): Promise<MonthlyReportPrecheckDto> => {
  const res = await apiClient.get<ApiSuccess<MonthlyReportPrecheckDto>>(
    `/monthly-batches/${batchId}/monthly-report-artifacts/precheck`,
    { params: { variant, locale, recipient_company_id: recipientCompanyId ?? undefined } },
  );
  return res.data.data;
};

export const generateMonthlyReportArtifact = async (
  batchId: string,
  request: MonthlyReportGenerateRequest,
): Promise<MonthlyReportGenerateDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportGenerateDto>>(
    `/monthly-batches/${batchId}/monthly-report-artifacts`,
    request,
  );
  return res.data.data;
};

export const fetchMonthlyReportArtifacts = async (
  batchId: string,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
  recipientCompanyId?: string | null,
): Promise<MonthlyReportArtifactListDto> => {
  const res = await apiClient.get<
    ApiSuccess<
      MonthlyReportArtifactListDto | MonthlyReportArtifactDto[],
      Partial<MonthlyReportArtifactListDto>
    >
  >(`/monthly-batches/${batchId}/monthly-report-artifacts`, {
    params: {
      variant,
      page,
      per_page: perPage,
      recipient_company_id: recipientCompanyId ?? undefined,
    },
  });
  const data = res.data.data;
  if (Array.isArray(data)) {
    return {
      items: data,
      page: Number(res.data.meta?.page ?? page),
      per_page: Number(res.data.meta?.per_page ?? perPage),
      total: Number(res.data.meta?.total ?? data.length),
      current_source_fingerprint: res.data.meta?.current_source_fingerprint ?? null,
    };
  }
  return {
    ...data,
    page: data.page ?? Number(res.data.meta?.page ?? page),
    per_page: data.per_page ?? Number(res.data.meta?.per_page ?? perPage),
    total: data.total ?? Number(res.data.meta?.total ?? data.items.length),
    current_source_fingerprint:
      data.current_source_fingerprint ?? res.data.meta?.current_source_fingerprint ?? null,
  };
};

const pagedListFromResponse = <T>(
  res: ApiSuccess<T[] | MonthlyReportPagedList<T>, Partial<MonthlyReportPagedList<T>>>,
  page: number,
  perPage: number,
): MonthlyReportPagedList<T> => {
  const data = res.data;
  if (Array.isArray(data)) {
    return {
      items: data,
      page: Number(res.meta?.page ?? page),
      per_page: Number(res.meta?.per_page ?? perPage),
      total: Number(res.meta?.total ?? data.length),
    };
  }
  return {
    ...data,
    page: data.page ?? Number(res.meta?.page ?? page),
    per_page: data.per_page ?? Number(res.meta?.per_page ?? perPage),
    total: data.total ?? Number(res.meta?.total ?? data.items.length),
  };
};

export const fetchMonthlyReportProfiles = async (
  batchId: string,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
): Promise<MonthlyReportPagedList<MonthlyReportProfileDto>> => {
  const res = await apiClient.get<
    ApiSuccess<
      MonthlyReportProfileDto[] | MonthlyReportPagedList<MonthlyReportProfileDto>,
      Partial<MonthlyReportPagedList<MonthlyReportProfileDto>>
    >
  >(`/monthly-batches/${batchId}/report-settings/profiles`, {
    params: { variant, page, per_page: perPage },
  });
  return pagedListFromResponse(res.data, page, perPage);
};

export const fetchMonthlyReportCommercialTerms = async (
  batchId: string,
  variant: MonthlyReportVariant,
  page: number,
  perPage: number,
): Promise<MonthlyReportPagedList<MonthlyReportCommercialTermsDto>> => {
  const res = await apiClient.get<
    ApiSuccess<
      MonthlyReportCommercialTermsDto[] | MonthlyReportPagedList<MonthlyReportCommercialTermsDto>,
      Partial<MonthlyReportPagedList<MonthlyReportCommercialTermsDto>>
    >
  >(`/monthly-batches/${batchId}/report-settings/commercial-terms`, {
    params: { variant, page, per_page: perPage },
  });
  return pagedListFromResponse(res.data, page, perPage);
};

export const lockMonthlyReportProfile = async (
  batchId: string,
  profileId: string,
): Promise<MonthlyReportProfileDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportProfileDto>>(
    `/monthly-batches/${batchId}/report-settings/profiles/${profileId}/lock`,
  );
  return res.data.data;
};

export const supersedeMonthlyReportProfile = async (
  batchId: string,
  profileId: string,
  reason?: string | null,
): Promise<MonthlyReportProfileDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportProfileDto>>(
    `/monthly-batches/${batchId}/report-settings/profiles/${profileId}/supersede`,
    { reason: reason || null },
  );
  return res.data.data;
};

export const lockMonthlyReportCommercialTerms = async (
  batchId: string,
  termSetId: string,
): Promise<MonthlyReportCommercialTermsDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportCommercialTermsDto>>(
    `/monthly-batches/${batchId}/report-settings/commercial-terms/${termSetId}/lock`,
  );
  return res.data.data;
};

export const supersedeMonthlyReportCommercialTerms = async (
  batchId: string,
  termSetId: string,
  reason?: string | null,
): Promise<MonthlyReportCommercialTermsDto> => {
  const res = await apiClient.post<ApiSuccess<MonthlyReportCommercialTermsDto>>(
    `/monthly-batches/${batchId}/report-settings/commercial-terms/${termSetId}/supersede`,
    { reason: reason || null },
  );
  return res.data.data;
};

export const monthlyReportArtifactDownloadUrl = (artifactId: string): string =>
  `/api/monthly-report-artifacts/${encodeURIComponent(artifactId)}/download`;

const contentDispositionFileName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.replace(/^"|"$/g, ''));
    } catch {
      return encoded.replace(/^"|"$/g, '');
    }
  }
  return value.match(/filename="?([^";]+)"?/i)?.[1] ?? null;
};

const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadMonthlyReportArtifact = async (
  artifactId: string,
  fallbackFileName: string,
): Promise<string> => {
  const res = await apiClient.get<Blob>(monthlyReportArtifactDownloadUrl(artifactId), {
    responseType: 'blob',
  });
  const fileName =
    contentDispositionFileName(res.headers?.['content-disposition']) ?? fallbackFileName;
  downloadBlob(res.data, fileName);
  return fileName;
};
